"use client"

import { useEffect } from "react"
import { PRODUCT_NAME } from "@/lib/product"
import { useBrandingStore, useBranding } from "@/stores/branding-store"

/**
 * Text colour that stays readable on top of `color`. The brand colour replaces
 * `--primary` in both themes, but the theme's own `--primary-foreground` was
 * picked for the default accent (dark text in dark mode), so a dark brand
 * colour ended up with near-black text on it. `color` may be hex or oklch(),
 * so it is resolved through a 1px canvas rather than parsed by hand.
 */
function readableForeground(color: string): string | null {
  const ctx = document.createElement("canvas").getContext("2d")
  if (!ctx) return null
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b] = Array.from(ctx.getImageData(0, 0, 1, 1).data).map((v) => {
    const c = v / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  // Same split WCAG uses: white wins on anything darker than ~mid-grey.
  return luminance > 0.179 ? "oklch(0.20 0.02 260)" : "oklch(0.995 0 0)"
}

/**
 * Designs this build ships a `[data-design]` token block for (app/globals.css).
 *
 * 'apollo' is the Phase 60 base in :root/.dark and has no block of its own — it
 * is what the attribute's absence already means. The allowlist is not injection
 * defence (`dataset` cannot inject); it stops a typo'd profile value from
 * silently landing a clinic on a design that does not exist, which would look
 * like "the redesign didn't ship" rather than like a config error.
 *
 * 'local' is the walk-in archetype's design (EKDent piloted it; albdental and
 * etrusk adopt it by profile key). It was called 'ekdent' for one day —
 * LEGACY_DESIGNS keeps a clinic whose env or cached value still says so on the
 * design rather than dropping it to apollo mid-deploy.
 */
const KNOWN_DESIGNS = new Set(["apollo", "local"])
const LEGACY_DESIGNS: Record<string, string> = { ekdent: "local" }

/** Mirrors DESIGN_SCOPE_KEY in app/layout.tsx — the pre-hydration cache. */
const DESIGN_CACHE_KEY = "dentex_app_design"

export function BrandingBootstrap() {
  const isLoaded = useBrandingStore((state) => state.isLoaded)
  const fetchBranding = useBrandingStore((state) => state.fetchBranding)
  const branding = useBranding()

  useEffect(() => {
    if (!isLoaded) {
      fetchBranding()
    }
  }, [isLoaded, fetchBranding])

  useEffect(() => {
    if (branding.primary_color) {
      document.documentElement.style.setProperty("--primary", branding.primary_color)
      document.documentElement.style.setProperty("--ring", branding.primary_color)
      document.documentElement.style.setProperty("--sidebar-primary", branding.primary_color)
      document.documentElement.style.setProperty("--sidebar-ring", branding.primary_color)
      const foreground = readableForeground(branding.primary_color)
      if (foreground) {
        document.documentElement.style.setProperty("--primary-foreground", foreground)
        document.documentElement.style.setProperty("--sidebar-primary-foreground", foreground)
      }
    }
    if (branding.name) {
      // Co-branded tab title: the clinic leads, the product signs.
      document.title = `${branding.name} — ${PRODUCT_NAME}`
    }
  }, [branding.primary_color, branding.name])

  // The clinic's app design (260910). Reconciles whatever DESIGN_SCOPE stamped
  // before hydration from the cache, and re-caches so the NEXT load paints the
  // right design in its first frame. Only runs once branding has actually
  // loaded: writing the fallback 'apollo' from the pre-fetch default would
  // flip a correctly-cached clinic back to the base design on every reload.
  useEffect(() => {
    if (!isLoaded) return

    const raw = branding.app_design ?? "apollo"
    const resolved = LEGACY_DESIGNS[raw] ?? raw
    const design = KNOWN_DESIGNS.has(resolved) ? resolved : "apollo"

    document.documentElement.dataset.design = design
    try {
      window.localStorage.setItem(DESIGN_CACHE_KEY, design)
    } catch {
      // Private mode / storage disabled — the app still renders correctly,
      // it just pays one frame of the base design on the next load.
    }
  }, [isLoaded, branding.app_design])

  return null
}
